import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { map } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import {
  useContentProviderDelete,
  useOrganizationContentProviders,
} from "../organizationServices";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ContentProviderDialog from "../edit/ContentProviderDialog";
import { AlertContext } from "../../components/alert/AlertProvider";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationContentProviderCard = ({ id }) => {
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);
  const [providers, setProviders] = useState([]);

  const providersResponse = useOrganizationContentProviders(id);
  useEffect(() => {
    if (providersResponse.isSuccess) {
      setProviders(providersResponse.data);
    }
  }, [providersResponse.isSuccess, providersResponse.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleProviderCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleProviderSave = useCallback(
    async data => {
      await providersResponse.refetch();
      setDialogOpen(false);
    },
    [providersResponse],
  );

  const providerDelete = useContentProviderDelete();
  const handleDeleteClick = useCallback(
    async providerId => {
      try {
        await providerDelete.mutateAsync({
          resourceIds: {
            orgId: id,
            contentProviderId: providerId,
          },
        });
        await providersResponse.refetch();
      } catch (err) {
        alertError(err);
      }
    },
    [id, providerDelete, providersResponse, alertError],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "subtitle1",
          color: "primary",
        }}
        title="Content Providers"
        action={
          <Tooltip title="Add Content Provider">
            <IconButton onClick={handleAddClick}>
              <AddIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {providersResponse.isError && (
          <Box padding={5} color="error" fontSize="1.4rem" fontStyle="italic">
            Error loading Content Providers.
          </Box>
        )}
        {providersResponse.isLoading && <PanelLoadingIndicator />}
        {providersResponse.isSuccess && isBlank(providers) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            No Content Providers set up for this Organization.
          </Box>
        )}
        {providersResponse.isSuccess && notBlank(providers) && (
          <List dense>
            {map(
              provider => (
                <ListItem key={provider.contentProviderId}>
                  <Tooltip title={provider.contentProviderId}>
                    <ListItemText
                      primaryTypographyProps={{
                        noWrap: true,
                        style: { fontSize: "1.2rem" },
                      }}
                      primary={provider.contentProviderId}
                    />
                  </Tooltip>
                  <ListItemSecondaryAction>
                    <ConfirmButton
                      handleDelete={() =>
                        handleDeleteClick(provider.contentProviderId)
                      }
                      dialogTitle="Remove Default Module"
                      dialogBody="Are you sure you want to remove this default module?"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ),
              providers,
            )}
          </List>
        )}
      </CardContent>

      {dialogOpen && (
        <ContentProviderDialog
          organizationId={id}
          open={dialogOpen}
          onCancel={handleProviderCancel}
          onSave={handleProviderSave}
        />
      )}
    </Card>
  );
};

export default OrganizationContentProviderCard;
